import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { toast } from "react-toastify";
import {
  PageContainer, PremiumCard, FormSection, Field, SelectField, Button,
} from "components/ui";

const BranchForm = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: "", code: "", city: "", address: "",
    phone: "", email: "", status: "ACTIVE",
    openingTime: "", closingTime: "", defaultCurrency: "TND",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await axiosPrivate.get(`/api/branches/${id}`);
        const b = res.data;
        setForm({
          name: b.name || "", code: b.code || "", city: b.city || "",
          address: b.address || "", phone: b.phone || "", email: b.email || "",
          status: b.status || "ACTIVE",
          openingTime: b.openingTime ? b.openingTime.substring(0, 5) : "",
          closingTime: b.closingTime ? b.closingTime.substring(0, 5) : "",
          defaultCurrency: b.defaultCurrency || "TND",
        });
      } catch (_) {
        toast.error("Branch not found");
        navigate("/tenant/branches");
      }
    })();
  }, [id]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name required";
    if (!form.code.trim()) errs.code = "Code required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        openingTime: form.openingTime ? form.openingTime + ":00" : null,
        closingTime: form.closingTime ? form.closingTime + ":00" : null,
      };
      if (isEdit) {
        await axiosPrivate.put(`/api/branches/${id}`, payload);
        toast.success("Branch updated");
      } else {
        await axiosPrivate.post("/api/branches", payload);
        toast.success("Branch created");
      }
      navigate("/tenant/branches");
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer narrow>
      <PremiumCard title={isEdit ? "Edit branch" : "Add branch"} subtitle="Branch contact and operating details">
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gap: 24 }}>
            <FormSection title="Identity" columns={2}>
              <Field label="Name" required value={form.name} onChange={change("name")} error={errors.name} placeholder="Main branch" />
              <Field label="Code" required value={form.code} onChange={change("code")} error={errors.code} placeholder="BR001" />
            </FormSection>
            <FormSection title="Location" columns={2}>
              <Field label="City" value={form.city} onChange={change("city")} placeholder="Tunis" />
              <Field label="Address" value={form.address} onChange={change("address")} placeholder="123 Avenue Habib Bourguiba" />
            </FormSection>
            <FormSection title="Contact" columns={2}>
              <Field label="Phone" value={form.phone} onChange={change("phone")} placeholder="+216 71 234 567" />
              <Field label="Email" value={form.email} onChange={change("email")} placeholder="branch@example.com" />
            </FormSection>
            <FormSection title="Hours" columns={2}>
              <Field label="Opening time" type="time" value={form.openingTime} onChange={change("openingTime")} />
              <Field label="Closing time" type="time" value={form.closingTime} onChange={change("closingTime")} />
            </FormSection>
            <FormSection title="Settings" columns={2}>
              <SelectField label="Default currency" value={form.defaultCurrency} onChange={change("defaultCurrency")}>
                <option value="TND">TND</option><option value="USD">USD</option><option value="EUR">EUR</option>
              </SelectField>
              <SelectField label="Status" value={form.status} onChange={change("status")}>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </SelectField>
            </FormSection>
          </div>

          <div className="form-actionbar">
            <Button type="button" variant="secondary" onClick={() => navigate("/tenant/branches")}>Cancel</Button>
            <Button type="submit" variant="primary" loading={loading}>{isEdit ? "Update branch" : "Create branch"}</Button>
          </div>
        </form>
      </PremiumCard>
    </PageContainer>
  );
};

export default BranchForm;
